/**
 * Weight Freeze (v2) — shared weight arithmetic.
 *
 * Extracted so every section that recomputes chargeable weight (Catalogue,
 * Packing, and others — six recompute sites across the module) uses the
 * same functions and the same constants. Always import RULES.VOLUMETRIC_DIVISOR
 * and RULES.WEIGHT_TOLERANCE_KG from ./data/reference — never a literal
 * 5000 or 0.005.
 *
 * NO 0.5 kg slab rounding anywhere: this data bills in 0.75 kg steps, and
 * that convention belongs to the sibling Disputes module only.
 */
import {RULES} from './data/reference'

// The merchant's own L x B x H divisor (merchant setup), sent with the catalogue. Until it
// arrives — or when the merchant has none — RULES.VOLUMETRIC_DIVISOR (5000) applies.
let volumetricDivisor = RULES.VOLUMETRIC_DIVISOR

/** Use the merchant's divisor from now on; anything missing or not positive falls back to 5000. */
export function setVolumetricDivisor(divisor) {
  const n = Number(divisor)
  volumetricDivisor = n > 0 ? n : RULES.VOLUMETRIC_DIVISOR
}

/** The divisor every volumetric weight in this module is worked out on. */
export function getVolumetricDivisor() {
  return volumetricDivisor
}

/** Round to 2 decimal places. */
export function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100
}

/** Round to 3 decimal places — the per-line dead x qty step, summed afterward with round2. */
export function round3(n) {
  return Math.round((Number(n) || 0) * 1000) / 1000
}

/**
 * Reads `[l, b, h]` out of either an array or an `{l, b, h}` object — the two
 * shapes `volumetricKg` below accepts. Returns null only when `dims` itself
 * is absent; a caller decides whether the resulting triple is complete or
 * positive enough to use (see e.g. packing/state/packingModel.js's own
 * `volumetric`, which applies exactly that gate).
 *
 * Section 0 consolidation: this used to be defined byte-identically in three
 * separate files (components/CartonSVG.js, packing/components/CartonFigure.js,
 * packing/components/VolWorking.js), locked together only by a cross-file
 * equality test rather than a shared source. Single-sourced here — the one
 * place `volumetricKg` itself already did this normalisation inline — and
 * imported by all three rather than redefined.
 */
export function toTriple(dims) {
  if (!dims) return null
  return Array.isArray(dims) ? dims : [dims.l, dims.b, dims.h]
}

/**
 * Volumetric weight = L x B x H / the merchant's divisor (getVolumetricDivisor), to 2dp.
 * Accepts dims as [l, b, h] or {l, b, h}. Flyers carry no dimensions at
 * all, so null/undefined/absent dims return 0 — dead weight always
 * governs for them.
 */
export function volumetricKg(dims) {
  const triple = toTriple(dims)
  if (!triple) return 0
  const [l, b, h] = triple
  if (l == null || b == null || h == null) return 0
  return round2((l * b * h) / volumetricDivisor)
}

/** Chargeable weight = the greater of dead and volumetric, to 2dp. */
export function chargeableKg(deadKg, volKg) {
  return round2(Math.max(Number(deadKg) || 0, Number(volKg) || 0))
}

/** True when two weights agree within RULES.WEIGHT_TOLERANCE_KG. */
export function withinTolerance(a, b) {
  return Math.abs((Number(a) || 0) - (Number(b) || 0)) <= RULES.WEIGHT_TOLERANCE_KG
}

/** Dimensioned cartons render their L x B x H triple; undimensioned flyers render just the label. */
export function packLabel(pack) {
  if (!pack) return ''
  const {label, dims} = pack
  if (!dims) return label
  const [l, b, h] = Array.isArray(dims) ? dims : [dims.l, dims.b, dims.h]
  return `${label} (${l}×${b}×${h} cm)`
}
